/**
 * Notification Handlers
 * Provides handlers for displaying notifications on API request success/error
 */

import {
  ApiError,
  ApiResponse,
  ErrorNotificationHandler,
  RequestConfig,
  SuccessNotificationHandler
} from '../types';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling';

/**
 * Configuration for notification handlers
 */
export interface NotificationConfig {
  // Function to show success notifications
  showSuccess: (message: string, title?: string) => void;
  // Function to show error notifications
  showError: (message: string, title?: string) => void;
  // Whether to show success notifications by default
  defaultShowSuccess?: boolean;
  // Whether to show error notifications by default
  defaultShowError?: boolean;
  // Custom message formatter for success notifications
  formatSuccessMessage?: (response: ApiResponse, config: RequestConfig) => string;
  // Custom message formatter for error notifications
  formatErrorMessage?: (error: ApiError, config: RequestConfig) => string;
}

/**
 * Creates a success notification handler
 * @param config - Notification configuration
 * @returns Success notification handler function
 */
export const createSuccessNotificationHandler = (
  config: NotificationConfig
): SuccessNotificationHandler => {
  return (response, requestConfig) => {
    // Check if notifications should be shown for this request
    const showNotification = requestConfig.headers?.['X-Show-Success-Notification'] !== 'false' && 
      (requestConfig.headers?.['X-Show-Success-Notification'] === 'true' || config.defaultShowSuccess);

    if (showNotification) {
      let message: string;

      if (config.formatSuccessMessage) {
        // Use custom message formatter if provided
        message = config.formatSuccessMessage(response, requestConfig);
      } else {
        // Default success message
        const method = requestConfig.method.toUpperCase();
        message = `Operation completed successfully`;

        // Add more context based on the request method
        if (method === 'POST') {
          message = 'Created successfully';
        } else if (method === 'PUT' || method === 'PATCH') {
          message = 'Updated successfully';
        } else if (method === 'DELETE') {
          message = 'Deleted successfully';
        }
      }

      // Show the notification
      config.showSuccess(message);
    }
  };
};

/**
 * Creates an error notification handler
 * @param config - Notification configuration
 * @returns Error notification handler function
 */
export const createErrorNotificationHandler = (
  config: NotificationConfig
): ErrorNotificationHandler => {
  return (error, requestConfig) => {
    // Check if notifications should be shown for this request
    const showNotification = requestConfig.headers?.['X-Show-Error-Notification'] !== 'false' && 
      (requestConfig.headers?.['X-Show-Error-Notification'] === 'true' || config.defaultShowError);

    if (showNotification) {
      let message: string;

      if (config.formatErrorMessage) {
        // Use custom message formatter if provided
        message = config.formatErrorMessage(error, requestConfig);
      } else {
        // Use getUserFriendlyErrorMessage for consistent error formatting
        message = getUserFriendlyErrorMessage(error);
      }

      // Show the notification
      config.showError(message);
    }
  };
};
